/**
 * Copyright (c) 2019 Vendor Badge, LLC All Rights Reserved.
 * Created Date: Monday, 9th Sept 2019
 *
 *  User Profile View
 */
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import TextFieldWithValidation from 'components/InputFields/TextFieldWithValidation';

// Material UI Components
import AddressForm from 'components/UserProfile/Address';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import InputAdornment from '@material-ui/core/InputAdornment';
import EmailIcon from '@material-ui/icons/Email';
import PhoneIcon from '@material-ui/icons/PhoneAndroid';
import AvatarInput from 'components/InputFields/Avatar';
import CircularProgress from '@material-ui/core/CircularProgress';
import { useStores } from 'stores/RootStore';

const Profile = props => {
  const { classes } = props;
  const stores = useStores();
  const {
    user,
    profile,
    hasChanges,
    handleInputChange,
    handleProfileInputChange,
    handleAddressInputChange,
    handleAvatarChange,
    isSaving,
    onSubmit,
  } = stores.userStore;

  // Update AppStore Page View
  stores.appStore.setView('Profile');

  return (
    <Grid container direction='column'>
      <Grid item xs={12} lg={10} xl={9}>
        <Grid container className={classes.gridContainer}>
          <Grid item sm={12} className={classes.addressGridItem}>
            <Grid container direction='column'>
              <form id='user-profile' autoComplete='off' onSubmit={onSubmit}>
                <Grid container justify='center' className={classes.gridContainer}>
                  <Grid item sm={6} xs={12} className={classes.addressGridItem}>
                    <Grid container>
                      <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                        What should we call you?
                      </Typography>
                      <Divider className={classes.sectionDivider} />
                      <Grid item sm={5} xs={12}>
                        <TextFieldWithValidation
                          id='firstName'
                          name='FirstName'
                          type='text'
                          required
                          visible
                          message='First name is required.'
                          value={user.firstName || ''}
                          handleInputChange={handleInputChange}
                          label='First Name'
                          className={classes.firstName}
                          fullWidth
                          margin='dense'
                          InputProps={{
                            className: classes.inputText,
                          }}
                          InputLabelProps={{
                            className: classes.inputLabelText,
                          }}
                        />
                      </Grid>
                      <Grid item sm={7} xs={12}>
                        <TextFieldWithValidation
                          id='lastName'
                          name='LastName'
                          type='text'
                          required
                          visible
                          message='Last name is required.'
                          label='Last Name'
                          value={user.lastName || ''}
                          handleInputChange={handleInputChange}
                          className={classes.lastName}
                          fullWidth
                          margin='dense'
                          InputProps={{
                            className: classes.inputText,
                          }}
                          InputLabelProps={{
                            className: classes.inputLabelText,
                          }}
                        />
                      </Grid>
                      <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                        How do we reach you?
                      </Typography>
                      <Divider className={classes.sectionDivider} />
                      <Grid item sm={5} xs={12}>
                        <TextFieldWithValidation
                          id='phoneNumber'
                          name='PhoneNumber'
                          type='text'
                          required
                          visible
                          pattern='^1?[-\. ]?(\(\d{3}\)?[-\. ]?|\d{3}?[-\. ]?)?\d{3}?[-\. ]?\d{4}$'
                          message='A valid U.S. phone number is required.'
                          label='Phone'
                          value={profile.phoneNumber || ''}
                          handleInputChange={handleProfileInputChange}
                          className={classes.phoneNumber}
                          fullWidth
                          margin='dense'
                          InputLabelProps={{
                            className: classes.inputLabelText,
                          }}
                          InputProps={{
                            className: classes.inputText,
                            endAdornment: (
                              <InputAdornment>
                                <PhoneIcon className={classes.inputIcon} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item sm={7} xs={12}>
                        <TextFieldWithValidation
                          id='email'
                          name='Email'
                          type='email'
                          required
                          disabled={true}
                          visible
                          message='A valid email address is required.'
                          label='Email'
                          value={user.email}
                          handleInputChange={handleInputChange}
                          className={classes.emailAddress}
                          fullWidth
                          margin='dense'
                          InputLabelProps={{
                            className: classes.inputLabelText,
                          }}
                          InputProps={{
                            className: classes.inputText,
                            endAdornment: (
                              <InputAdornment>
                                <EmailIcon className={classes.inputIcon} />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography
                        variant='body1'
                        color='textSecondary'
                        className={classes.sectionTitle}
                        style={{ paddingTop: '11px' }}
                      >
                        How do we identify you?
                      </Typography>
                      <Divider className={classes.sectionDivider} />
                    </Grid>
                    <Grid
                      container
                      justify='center'
                      direction='column'
                      alignItems='center'
                      className={classes.avatarGrid}
                    >
                      <Grid item xs={12} className={classes.avatarInput}>
                        <AvatarInput required url={user.avatarUrl} handleAvatarChange={handleAvatarChange} />
                        {user.photoUrl}
                        <Typography variant='h6' color='textSecondary' className={classes.avatarFooter}>
                          Choose Picture{user.type === 'vendor' ? '*' : ''}
                        </Typography>
                        <Typography variant='caption' color='textSecondary'>
                          {'( gif, jpg, png )'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    {user.type === 'vendor' ? (
                      <Grid item xs={12}>
                        <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                          What is your address?
                        </Typography>
                        <Divider className={classes.sectionDivider} />
                        <AddressForm address1 address2 city state zip handleInputUpdate={handleAddressInputChange} />
                      </Grid>
                    ) : null}
                    <Typography variant='body1' color='textSecondary' className={classes.sectionTitle}>
                      Tell us your story...
                    </Typography>
                    <Divider className={classes.sectionDivider} />
                    <TextFieldWithValidation
                      id='bio'
                      name='Bio'
                      helperText='Visible to all VendorBadge users.'
                      multiline
                      rows='8'
                      visible
                      value={profile.bio}
                      handleInputChange={handleProfileInputChange}
                      fullWidth
                      margin='dense'
                      InputLabelProps={{
                        className: classes.inputLabelText,
                      }}
                      InputProps={{
                        className: classes.inputTextBio,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid item className={classes.buttonWrapper}>
                  <Button
                    disabled={!hasChanges || isSaving}
                    variant='contained'
                    type='submit'
                    color='primary'
                    className={classes.saveButton}
                  >
                    Save
                  </Button>
                  {isSaving && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

Profile.propTypes = {
  classes: PropTypes.object.isRequired,
};

const styles = theme => ({
  gridContainer: {},
  addressGridItem: {
    paddingLeft: '5px',
    paddingRight: '10px',
    paddingBottom: '5px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '5px',
    },
  },
  inputIcon: {
    color: theme.palette.type === 'light' ? 'rgb(128, 128, 128)' : theme.palette.common.white,
    padding: '0',
  },
  avatarGrid: {
    paddingLeft: '10px',
    paddingRight: '5px',
    paddingBottom: '5px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '5px',
    },
    textAlign: 'center',
  },
  avatarInput: {
    paddingTop: '3px',
  },
  avatarFooter: {
    fontSize: '10pt',
    textAlign: 'center',
  },
  sectionTitleGrid: {
    marginBottom: '10px',
  },
  sectionTitle: {
    marginLeft: '-5px',
    marginTop: '15px',
    fontWeight: 600,
    color: theme.palette.primary.light,
  },
  sectionDivider: {
    width: '100%',
    marginLeft: '-5px',
    marginBottom: '10px',
  },
  firstName: {
    padding: '2px',
    paddingRight: '10px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '5px',
    },
  },
  lastName: {
    padding: '2px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '5px',
    },
  },
  phoneNumber: {
    padding: '2px',
    paddingRight: '10px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '5px',
    },
  },
  emailAddress: {
    padding: '2px',
    [theme.breakpoints.down('xs')]: {
      paddingRight: '5px',
    },
  },
  inputLabelText: {
    fontSize: '11pt',
    [theme.breakpoints.down('xs')]: {
      fontSize: '10pt',
    },
  },
  inputTextBio: {
    fontSize: '10pt',
  },
  buttonWrapper: {
    position: 'relative',
  },
  saveButton: {
    marginTop: '25px',
  },
  buttonProgress: {
    zIndex: 1,
    position: 'absolute',
    marginTop: 31,
    marginLeft: -46,
  },
});

export default withStyles(styles, { withTheme: true })(observer(Profile));
